import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import { patchApplicationEvent, postApplicationEvent } from '@/api/Rover/ApplicationEvents';
import type { IPatchApplicationEventPayload, IPostApplicationEventPayload } from '@/api/Rover/ApplicationEvents/types';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookFormProvider, type HookFormProviderProps } from '@/components/UI/HookForm/Provider';
import Modal, { type ModalProps } from '@/components/UI/Modal';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { notifyUser } from '@/store/Toast';
import type { ApplicationPhaseType } from '@/types/ClientApplication';
import { formatDate3, isObjectEmpty, sanitizeResponseError } from '@/utils';

const validationSchema = Yup.object({
  name: Yup.string().required('Required').min(2, 'Please enter event name'),
  date: Yup.string().required('Required'),
});

export interface SubmitValues {
  id?: number | string;
  name: string;
  date: string;
}
interface ApplicationEventFormModalBaseProps extends ModalProps {
  hookFormConfig?: HookFormProviderProps;
  title: string;
  phase: ApplicationPhaseType;
  handleModal: () => void;
}
const ApplicationEventFormModalBase = ({ hookFormConfig, title, handleModal, phase, ...rest }: ApplicationEventFormModalBaseProps) => {
  const {
    watch,
    formState: { touchedFields },
  } = useFormContext();
  const params = useParams();
  const id = watch('id');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const createApplicationEvent = useMutation(
    (data: IPostApplicationEventPayload) =>
      postApplicationEvent({
        applicationId: params.applicationId!,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_EVENTS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Application Event created successfully.',
            toastType: 'success',
          })
        );
        handleModal();
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateApplicationEvent = useMutation(
    (data: Partial<IPatchApplicationEventPayload>) =>
      patchApplicationEvent({
        applicationId: params.applicationId!,
        eventId: id,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_EVENTS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Application Event updated successfully.',
            toastType: 'success',
          })
        );
        handleModal();
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSubmitApplicationEvent = (values: SubmitValues) => {
    if (values.id) {
      const updateData: Partial<IPatchApplicationEventPayload> = {
        name: values.name,
        date: formatDate3(values.date, 'mm/dd/yyyy'),
      };
      if (!isObjectEmpty(touchedFields)) {
        updateApplicationEvent.mutate(updateData);
      } else {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'No changes were detected.',
            toastType: 'info',
          })
        );
      }
    } else {
      const createData: IPostApplicationEventPayload = {
        name: values.name,
        date: formatDate3(values.date, 'mm/dd/yyyy'),
        phase,
      };
      createApplicationEvent.mutate(createData);
    }
  };

  return (
    <Modal {...rest}>
      <h2 className='mt-0 text-primaryColor-normal capitalize'>{`${id ? 'Edit' : 'Add'} ${title}`}</h2>
      <HookForm onSubmit={handleSubmitApplicationEvent}>
        <div className='space-y-4'>
          <HookInput type='text' name='name' label='Event Name' />
          <HookInput type='date' name='date' label='Event Date' />
          <div className='flex gap-4 justify-end'>
            <Button
              disabled={createApplicationEvent.isLoading || updateApplicationEvent.isLoading}
              label='Cancel'
              type='button'
              btnStyle='btn-neutral'
              onClick={handleModal}
            />
            <Button
              disabled={createApplicationEvent.isLoading || updateApplicationEvent.isLoading}
              label={createApplicationEvent.isLoading || updateApplicationEvent.isLoading ? 'Please Wait...' : 'Submit'}
              type='submit'
              btnStyle='btn-primary'
            />
          </div>
        </div>
      </HookForm>
    </Modal>
  );
};

export const ApplicationEventFormModal = ({ hookFormConfig, ...rest }: ApplicationEventFormModalBaseProps) => (
  <HookFormProvider {...hookFormConfig} resolver={yupResolver(validationSchema) as any}>
    <ApplicationEventFormModalBase {...rest} />
  </HookFormProvider>
);
